import React from "react";
/**
 * Unified Session Management System
 * Handles all time tracking as sessions (manual and timer)
 */

import db from "./db";

const STORES = {
  PROJECTS: "projects",
  TASKS: "tasks",
  TIMER_SESSIONS: "timerSessions",
};

export class SessionManager {
  constructor() {
    this.listeners = [];
  }

  // Session CRUD Operations
  async createSession(taskId, sessionData) {
    const now = new Date().toISOString();
    console.log("🔵 SessionManager: Creating session");
    console.log("  📋 Task ID:", taskId);
    console.log("  📊 Session Data:", sessionData);
    console.log("  ⏰ Current time:", now);

    // Ensure all date fields are valid
    const createdAt =
      sessionData.createdAt && !isNaN(new Date(sessionData.createdAt).getTime())
        ? sessionData.createdAt
        : now;
    const startTime =
      sessionData.startTime && !isNaN(new Date(sessionData.startTime).getTime())
        ? sessionData.startTime
        : now;
    const endTime =
      sessionData.endTime && !isNaN(new Date(sessionData.endTime).getTime())
        ? sessionData.endTime
        : now;

    const sessionId = this.generateId();
    console.log("  🆔 Generated session ID:", sessionId);

    const session = {
      id: sessionId,
      taskId: taskId,
      duration: sessionData.duration,
      type: sessionData.type || "manual",
      description: sessionData.description || "",
      createdAt: createdAt,
      startTime: startTime,
      endTime: endTime,
    };

    console.log("  ✅ Final session object:", session);
    console.log("  💾 Saving to database...");
    await db.add(STORES.TIMER_SESSIONS, session);
    console.log("  🔔 Notifying listeners...");
    this.notifyListeners("session_created", session);
    console.log("  🎉 Session created successfully!");
    return session;
  }

  async updateSession(sessionId, updates) {
    console.log("🟡 SessionManager: Updating session");
    console.log("  🆔 Session ID:", sessionId);
    console.log("  📝 Updates:", updates);

    const session = await db.get(STORES.TIMER_SESSIONS, sessionId);
    if (!session) {
      console.error("  ❌ Session not found:", sessionId);
      throw new Error("Session not found");
    }
    console.log("  📋 Found session:", session);

    // Validate date fields if they're being updated
    const validatedUpdates = { ...updates };
    if (updates.createdAt && isNaN(new Date(updates.createdAt).getTime())) {
      console.warn(
        "SessionManager: Invalid createdAt provided, using current time"
      );
      validatedUpdates.createdAt = new Date().toISOString();
    }
    if (updates.startTime && isNaN(new Date(updates.startTime).getTime())) {
      console.warn(
        "SessionManager: Invalid startTime provided, using current time"
      );
      validatedUpdates.startTime = new Date().toISOString();
    }
    if (updates.endTime && isNaN(new Date(updates.endTime).getTime())) {
      console.warn(
        "SessionManager: Invalid endTime provided, using current time"
      );
      validatedUpdates.endTime = new Date().toISOString();
    }

    const updatedSession = {
      ...session,
      ...validatedUpdates,
      updatedAt: new Date().toISOString(),
    };
    await db.update(STORES.TIMER_SESSIONS, updatedSession);
    this.notifyListeners("session_updated", updatedSession);
    return updatedSession;
  }

  async deleteSession(sessionId) {
    const session = await db.get(STORES.TIMER_SESSIONS, sessionId);
    if (!session) throw new Error("Session not found");

    await db.delete(STORES.TIMER_SESSIONS, sessionId);
    this.notifyListeners("session_deleted", session);
    return session;
  }

  async getSessionsByTask(taskId) {
    console.log("SessionManager: Getting sessions for task:", taskId);
    const allSessions = await this.getAllSessions(); // This will clean up dates
    console.log("SessionManager: All sessions from DB (cleaned):", allSessions);
    const filteredSessions = allSessions.filter(
      (session) => session.taskId === taskId
    );
    console.log(
      "SessionManager: Filtered sessions for task:",
      filteredSessions
    );
    return filteredSessions;
  }

  async getAllSessions() {
    console.log("🔍 SessionManager: Getting all sessions from DB");
    const sessions = await db.getAll(STORES.TIMER_SESSIONS);
    console.log("  📊 Raw sessions from DB:", sessions);

    // Clean up any sessions with invalid dates
    const cleanedSessions = sessions.map((session, index) => {
      console.log(`SessionManager: Processing session ${index}:`, session);
      const now = new Date().toISOString();
      const cleanedSession = {
        ...session,
        id: session.id || `generated-${Date.now()}-${index}`, // Ensure ID exists
        createdAt:
          session.createdAt && !isNaN(new Date(session.createdAt).getTime())
            ? session.createdAt
            : now,
        startTime:
          session.startTime && !isNaN(new Date(session.startTime).getTime())
            ? session.startTime
            : now,
        endTime:
          session.endTime && !isNaN(new Date(session.endTime).getTime())
            ? session.endTime
            : now,
      };
      console.log(`SessionManager: Cleaned session ${index}:`, cleanedSession);
      return cleanedSession;
    });
    console.log("SessionManager: All cleaned sessions:", cleanedSessions);
    return cleanedSessions;
  }

  // Time Calculation
  calculateTotalTime(sessions) {
    return sessions.reduce((total, session) => total + session.duration, 0);
  }

  calculateTimeByType(sessions) {
    const manualSessions = sessions.filter((s) => s.type === "manual");
    const timerSessions = sessions.filter((s) => s.type === "timer");

    return {
      manual: this.calculateTotalTime(manualSessions),
      timer: this.calculateTotalTime(timerSessions),
      total: this.calculateTotalTime(sessions),
      manualCount: manualSessions.length,
      timerCount: timerSessions.length,
    };
  }

  // Session Analysis
  getSessionStats(sessions) {
    if (sessions.length === 0) {
      return {
        totalTime: 0,
        averageSession: 0,
        longestSession: 0,
        shortestSession: 0,
        totalSessions: 0,
        manualTime: 0,
        timerTime: 0,
      };
    }

    const durations = sessions.map((s) => s.duration);
    const { manual, timer, total } = this.calculateTimeByType(sessions);

    return {
      totalTime: total,
      averageSession: total / sessions.length,
      longestSession: Math.max(...durations),
      shortestSession: Math.min(...durations),
      totalSessions: sessions.length,
      manualTime: manual,
      timerTime: timer,
    };
  }

  // Session Grouping
  groupSessionsByDate(sessions) {
    const groups = {};

    sessions.forEach((session) => {
      const date = new Date(session.createdAt).toDateString();
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(session);
    });

    return groups;
  }

  groupSessionsByProject(sessions, tasks, projects) {
    const groups = {};

    sessions.forEach((session) => {
      const task = tasks.find((t) => t.id === session.taskId);
      if (!task) return;

      const project = projects.find((p) => p.id === task.projectId);
      const projectName = project ? project.name : "No Project";

      if (!groups[projectName]) {
        groups[projectName] = [];
      }
      groups[projectName].push(session);
    });

    return groups;
  }

  // Utility Methods
  generateId() {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  // Event System
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(
        (listener) => listener !== callback
      );
    };
  }

  notifyListeners(event, data) {
    this.listeners.forEach((listener) => {
      try {
        listener(event, data);
      } catch (error) {
        console.error("Error in session manager listener:", error);
      }
    });
  }

  // Validation
  validateSession(sessionData) {
    const errors = [];

    if (!sessionData.duration || sessionData.duration <= 0) {
      errors.push("Duration must be greater than 0");
    }

    if (!sessionData.taskId) {
      errors.push("Task ID is required");
    }

    if (sessionData.type && !["manual", "timer"].includes(sessionData.type)) {
      errors.push('Type must be either "manual" or "timer"');
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  }

  // Bulk Operations
  async deleteSessionsByTask(taskId) {
    const sessions = await this.getSessionsByTask(taskId);
    const deletePromises = sessions.map((session) =>
      this.deleteSession(session.id)
    );
    await Promise.all(deletePromises);
    return sessions.length;
  }

  async duplicateSession(sessionId, newTaskId = null) {
    const session = await db.get(STORES.TIMER_SESSIONS, sessionId);
    if (!session) throw new Error("Session not found");

    const duplicatedSession = {
      ...session,
      id: this.generateId(),
      taskId: newTaskId || session.taskId,
      createdAt: new Date().toISOString(),
      description: session.description + " (copy)",
    };

    delete duplicatedSession.updatedAt;
    return await this.createSession(
      duplicatedSession.taskId,
      duplicatedSession
    );
  }
}

// Create singleton instance
export const sessionManager = new SessionManager();

// React Hook for Session Management
export const useSessionManager = (taskId = null) => {
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const loadSessions = async () => {
      setLoading(true);
      setError(null);
      try {
        const sessionData = taskId
          ? await sessionManager.getSessionsByTask(taskId)
          : await sessionManager.getAllSessions();
        setSessions(sessionData);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadSessions();

    // Listen for session changes
    const unsubscribe = sessionManager.addListener((event, data) => {
      if (
        event === "session_created" ||
        event === "session_updated" ||
        event === "session_deleted"
      ) {
        // If we're filtering by taskId, only reload if the session belongs to this task
        if (taskId && data.taskId !== taskId) {
          return;
        }
        loadSessions(); // Reload sessions
      }
    });

    return unsubscribe;
  }, [taskId]);

  const createSession = React.useCallback(
    async (sessionData) => {
      try {
        // For new tasks, we need to validate without taskId first
        const validationData = { ...sessionData, taskId: taskId || "temp" };
        const validation = sessionManager.validateSession(validationData);
        if (!validation.isValid) {
          throw new Error(validation.errors.join(", "));
        }

        // If no taskId, we can't create the session yet
        if (!taskId) {
          throw new Error(
            "Cannot create session without a task ID. Please save the task first."
          );
        }

        return await sessionManager.createSession(taskId, sessionData);
      } catch (err) {
        setError(err.message);
        throw err;
      }
    },
    [taskId]
  );

  const updateSession = React.useCallback(async (sessionId, updates) => {
    try {
      return await sessionManager.updateSession(sessionId, updates);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const deleteSession = React.useCallback(async (sessionId) => {
    try {
      return await sessionManager.deleteSession(sessionId);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  }, []);

  const stats = React.useMemo(() => {
    return sessionManager.getSessionStats(sessions);
  }, [sessions]);

  const timeBreakdown = React.useMemo(() => {
    return sessionManager.calculateTimeByType(sessions);
  }, [sessions]);

  return {
    sessions,
    loading,
    error,
    stats,
    timeBreakdown,
    createSession,
    updateSession,
    deleteSession,
    refresh: () => {
      const loadSessions = async () => {
        setLoading(true);
        setError(null);
        try {
          const sessionData = taskId
            ? await sessionManager.getSessionsByTask(taskId)
            : await sessionManager.getAllSessions();
          setSessions(sessionData);
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      loadSessions();
    },
  };
};
